import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Paper,
    Box,
    Button
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

interface ViewIssueDialogProps {
    open: boolean;
    onClose: () => void;
    ticketData?: any;
}

const headerCellStyle = {
    backgroundColor: '#f5f5f5',
    fontWeight: 'bold',
    width: '20%',
    borderBottom: '1px solid #e0e0e0',
    borderRight: '1px solid #e0e0e0',
    textAlign: 'center',
    padding: '12px'
};

const valueCellStyle = {
    width: '30%',
    borderBottom: '1px solid #e0e0e0',
    borderRight: '1px solid #e0e0e0',
    padding: '12px'
};

const ViewIssueDialog: React.FC<ViewIssueDialogProps> = ({ open, onClose, ticketData }) => {
    if (!ticketData) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: 2,
                    width: '100%',
                    maxWidth: '800px',
                }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '16px 24px',
                borderBottom: 'none'
            }}>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#333' }}>
                    Ticket Details
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        color: '#000',
                        '&:hover': {
                            backgroundColor: 'rgba(0,0,0,0.04)'
                        }
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ padding: '0px 24px 24px 24px' }}>
                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 0 }}>
                    <Table sx={{ minWidth: 650 }}>
                        <TableBody>
                            {/* Row 1: Ticket Number & Subject */}
                            <TableRow>
                                <TableCell sx={headerCellStyle}>Ticket<br />Number</TableCell>
                                <TableCell sx={valueCellStyle}>{ticketData.ticketNumber || 'N/A'}</TableCell>
                                <TableCell sx={headerCellStyle}>Subject</TableCell>
                                <TableCell sx={valueCellStyle}>{ticketData.subject || 'N/A'}</TableCell>
                            </TableRow>

                            {/* Row 2: Issue (Spans across all remaining columns) */}
                            <TableRow>
                                <TableCell sx={headerCellStyle}>Issue</TableCell>
                                <TableCell colSpan={3} sx={{ ...valueCellStyle, borderRight: 'none' }}>
                                    {ticketData.issue || 'N/A'}
                                </TableCell>
                            </TableRow>

                            {/* Row 3: Attached File & Attached Screenshot */}
                            <TableRow>
                                <TableCell sx={headerCellStyle}>Attached File</TableCell>
                                <TableCell sx={valueCellStyle}>
                                    {ticketData.attached_filename_url ? (
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() => window.open(ticketData.attached_filename_url, '_blank')}
                                            sx={{
                                                borderRadius: '24px',
                                                padding: '4px 16px',
                                                borderColor: '#58b80f',
                                                color: '#58b80f',
                                                fontSize: '12px',
                                                backgroundColor: 'rgba(14, 248, 119, 0.1)',
                                                textTransform: 'uppercase',
                                                minWidth: '100px',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(168, 227, 195, 0.2)',
                                                    borderColor: '#58b80f'
                                                }
                                            }}
                                        >
                                            PREVIEW
                                        </Button>
                                    ) : (
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            disabled
                                            sx={{
                                                borderRadius: '24px',
                                                padding: '4px 16px',
                                                borderColor: '#e0e0e0',
                                                color: '#bbb',
                                                fontSize: '12px',
                                                backgroundColor: '#f5f5f5',
                                                textTransform: 'uppercase',
                                                minWidth: '60px',
                                                border: '1px solid #e0e0e0',
                                                '&.Mui-disabled': {
                                                    borderColor: '#e0e0e0',
                                                    color: '#bbb'
                                                }
                                            }}
                                        >
                                            N/A
                                        </Button>
                                    )}
                                </TableCell>
                                <TableCell sx={headerCellStyle}>Attached<br />Screenshot</TableCell>
                                <TableCell sx={valueCellStyle}>
                                    {ticketData.attached_screenshot_url ? (
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() => window.open(ticketData.attached_screenshot_url, '_blank')}
                                            sx={{
                                                borderRadius: '24px',
                                                padding: '4px 16px',
                                                borderColor: '#58b80f',
                                                color: '#58b80f',
                                                fontSize: '12px',
                                                backgroundColor: 'rgba(14, 248, 119, 0.1)',
                                                textTransform: 'uppercase',
                                                minWidth: '100px',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(168, 227, 195, 0.2)',
                                                    borderColor: '#58b80f'
                                                }
                                            }}
                                        >
                                            PREVIEW
                                        </Button>
                                    ) : (
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            disabled
                                            sx={{
                                                borderRadius: '24px',
                                                padding: '4px 16px',
                                                borderColor: '#e0e0e0',
                                                color: '#bbb',
                                                fontSize: '12px',
                                                backgroundColor: '#f5f5f5',
                                                textTransform: 'uppercase',
                                                minWidth: '60px',
                                                border: '1px solid #e0e0e0',
                                                '&.Mui-disabled': {
                                                    borderColor: '#e0e0e0',
                                                    color: '#bbb'
                                                }
                                            }}
                                        >
                                            N/A
                                        </Button>
                                    )}
                                </TableCell>
                            </TableRow>

                            {/* Row 4: Comment */}
                            <TableRow>
                                <TableCell sx={{ ...headerCellStyle, borderBottom: 'none' }}>Comment</TableCell>
                                <TableCell colSpan={3} sx={{ ...valueCellStyle, borderBottom: 'none', borderRight: 'none' }}>
                                    {ticketData.comment || 'N/A'}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>
        </Dialog>
    );
};

export default ViewIssueDialog;
